import type { CmsEditableAction, CmsEditableElementController as Controller, CmsElementTypeMeta } from "./types";

export class CmsEditableElementController implements Controller {
    constructor(
        private readonly element: HTMLElement,
        private readonly elementTypes: Record<string, string> = {},
        private readonly elementTypeMeta: Record<string, CmsElementTypeMeta> = {},
    ) {}

    mount(): void {
        this.element.tabIndex = this.element.tabIndex >= 0 ? this.element.tabIndex : 0;
        this.element.classList.add("is-cms-editable-mounted");
    }

    select(): void {
        this.element.classList.add("is-selected");
        this.element.setAttribute("aria-selected", "true");
    }

    unselect(): void {
        this.element.classList.remove("is-selected");
        this.element.removeAttribute("aria-selected");
    }

    destroy(): void {
        this.unselect();
        this.element.classList.remove("is-cms-editable-mounted");
    }

    getElement(): HTMLElement {
        return this.element;
    }

    getActions(): CmsEditableAction[] {
        const type = this.element.dataset.cmsEditableType ?? "element";
        const typeLabel = this.element.dataset.typeLabel ?? type;
        const editUrl = this.element.dataset.editUrl ?? "";
        const updateUrl = this.element.dataset.updateUrl ?? "";
        const duplicateUrl = this.element.dataset.duplicateUrl ?? "";
        const deleteUrl = this.element.dataset.deleteUrl ?? "";
        const addElementUrl = this.element.dataset.addElementUrl ?? "";
        const sectionType = this.element.dataset.sectionType ?? "";
        const currentElementType = this.element.dataset.elementType ?? "";
        const actions: CmsEditableAction[] = [];

        if (editUrl) {
            actions.push({
                key: "edit",
                label: type === "section" ? "섹션 설정" : "블록 설정",
                icon: "fa-solid fa-pen",
                intent: "inspect",
                url: editUrl,
                title: type === "section" ? "섹션 설정" : "블록 설정",
            });
        }
        if (type === "section" && sectionType !== "template" && addElementUrl) {
            actions.push({
                key: "add",
                label: "블록 추가",
                icon: "fa-solid fa-plus",
                intent: "add-element",
                url: addElementUrl,
                title: "블록 추가",
            });
        }
        if (type === "element" && Object.keys(this.elementTypes).length > 0) {
            actions.push({
                key: "type",
                label: "블록 유형",
                icon: "fa-solid fa-shapes",
                intent: "type-change",
                url: updateUrl,
                value: currentElementType,
                options: this.elementTypes,
                optionMeta: this.elementTypeMeta,
            });
        }
        if (type === "element" && currentElementType === "image" && !this.element.classList.contains("is-empty")) {
            actions.push({
                key: "preview",
                label: "미리보기 확대",
                icon: "fa-solid fa-expand",
                intent: "preview-toggle",
            });
        }
        actions.push({
            key: "up",
            label: "위로 이동",
            icon: "fa-solid fa-arrow-up",
            intent: "move-up",
        });
        actions.push({
            key: "down",
            label: "아래로 이동",
            icon: "fa-solid fa-arrow-down",
            intent: "move-down",
        });
        if (duplicateUrl) {
            actions.push({
                key: "duplicate",
                label: "복제",
                icon: "fa-regular fa-clone",
                intent: "post",
                url: duplicateUrl,
            });
        }
        if (deleteUrl) {
            actions.push({
                key: "delete",
                label: "삭제",
                icon: "fa-regular fa-trash-can",
                intent: "delete",
                url: deleteUrl,
                confirm: this.element.dataset.deleteConfirm ?? "삭제할까요?",
            });
        }

        return actions;
    }
}
